<template>
  <!-- Products + date range filter. Mirrors `ServicesFilter` but for the
       products store. Value shape unchanged:
         { products: Product[], date: { start, finish } } -->
  <FilterControlGroup>
    <div class="space-y-1">
      <label class="text-xs font-medium text-gray-600 dark:text-gray-400">
        {{ $t('components.customer.list.fieldsWithDateRange.during') }}
      </label>
      <DateRangeFilter v-model="modelValue.date" />
    </div>

    <div class="space-y-1">
      <div class="flex items-center justify-between">
        <label class="text-xs font-medium text-gray-600 dark:text-gray-400">
          {{ $t('general.products') }}
          <span v-if="modelValue.products.length" class="text-primary-500">
            ({{ modelValue.products.length }})
          </span>
        </label>
        <button
          v-if="modelValue.products.length"
          type="button"
          class="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          @click="clearProducts"
        >
          {{ $t('general.clear') }}
        </button>
      </div>
      <ModelMultiSelect
        :model-value="modelValue.products"
        :items="productItems"
        :placeholder="$t('product.all')"
        by="idProduct"
        @update:model-value="(v: any) => onProductsChange(v)"
      />
    </div>
  </FilterControlGroup>
</template>

<script setup lang="ts">
import DateRangeFilter from './DateRangeFilter.vue'
import FilterControlGroup from './FilterControlGroup.vue'
import ModelMultiSelect from './ModelMultiSelect.vue'

const modelValue = defineModel<{
  products: any[]
  date: { start: number | null; finish: number | null }
}>({
  default: () => ({ products: [], date: { start: null, finish: null } }),
})

const productStore = useProductStore()

// `productStore.all` returns `[{ category, products }, …]` — same shape as
// `serviceStore.categorised`. Render as listbox groups; in-category rows
// each carry the full product object so emitted selections are usable.
const productItems = computed(() => {
  const grouped = (productStore.all as any[]) ?? []
  return grouped
    .filter((g) => g.products?.length)
    .map((g) => {
      const items: any[] = []
      const name = g.category?.name ?? ''
      if (name) items.push({ type: 'label', label: name })
      for (const product of g.products) {
        items.push({ ...product, label: product.name ?? product.title })
      }
      return items
    })
})

const onProductsChange = (v: any[]) => {
  modelValue.value = { ...modelValue.value, products: Array.isArray(v) ? v : [] }
}

const clearProducts = () => {
  modelValue.value = { ...modelValue.value, products: [] }
}
</script>
